import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Phone, X, Loader2, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STRINGS = {
    en: {
        fab: 'Talk with us now!',
        title: 'Request a Callback',
        subtitle: 'A team member will call you back within business hours.',
        name: 'Your Name',
        namePh: 'First & Last Name',
        email: 'Email',
        emailPh: 'you@example.com',
        phone: 'Phone Number',
        phonePh: '(555) 123-4567',
        org: 'Organization (optional)',
        orgPh: 'Your nonprofit / org',
        submit: 'Request Callback',
        success: 'Got it!',
        successDetail: "We'll call you back shortly.",
        close: 'Close',
        errRequired: 'Please enter your name and phone.',
        errPhone: 'Please enter a valid phone number.',
        errSend: 'Could not submit. Please try again.',
    },
    es: {
        fab: '¡Habla con nosotros ahora!',
        title: 'Solicitar una Llamada',
        subtitle: 'Un miembro de nuestro equipo te llamará en horario laboral.',
        name: 'Tu Nombre',
        namePh: 'Nombre y Apellido',
        email: 'Correo Electrónico',
        emailPh: 'tu@ejemplo.com',
        phone: 'Número de Teléfono',
        phonePh: '(555) 123-4567',
        org: 'Organización (opcional)',
        orgPh: 'Tu organización sin fines de lucro',
        submit: 'Solicitar Llamada',
        success: '¡Listo!',
        successDetail: 'Te llamaremos pronto.',
        close: 'Cerrar',
        errRequired: 'Por favor ingresa tu nombre y teléfono.',
        errPhone: 'Por favor ingresa un número válido.',
        errSend: 'No se pudo enviar. Intenta de nuevo.',
    },
};

const detectLang = () => {
    if (typeof document !== 'undefined') {
        const htmlLang = (document.documentElement.lang || '').toLowerCase();
        if (htmlLang.startsWith('es')) return 'es';
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/es')) return 'es';
    }
    return 'en';
};

export default function CallbackWidget({ source = 'CF' }) {
    const lang = detectLang();
    const s = STRINGS[lang];

    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [org, setOrg] = useState('');
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const panelRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const handleSubmit = async () => {
        setError('');
        if (!name.trim() || !phone.trim()) {
            setError(s.errRequired);
            return;
        }
        if (phone.replace(/\D/g, '').length < 7) {
            setError(s.errPhone);
            return;
        }
        setSending(true);
        try {
            const cleanPhone = phone.replace(/\D/g, '');
            const formattedPhone = cleanPhone.startsWith('1') ? `+${cleanPhone}` : `+1${cleanPhone}`;
            await axios.post(`${API}/api/sms/popup/capture`, {
                variant_id: 'callback_request',
                name: name.trim(),
                email: email.trim(),
                phone: formattedPhone,
                organization: org.trim(),
                source,
                preferred_language: lang,
                page_url: typeof window !== 'undefined' ? window.location.href : '',
            });
            setSent(true);
        } catch (e) {
            console.error(e);
            setError(s.errSend);
        }
        setSending(false);
    };

    const reset = () => {
        setName(''); setEmail(''); setPhone(''); setOrg('');
        setSent(false); setError(''); setOpen(false);
    };

    return (
        <>
            {!open && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    data-testid="callback-fab-btn"
                    aria-label={s.fab}
                    className="fixed left-3 bottom-3 sm:left-6 sm:bottom-6 z-[60] flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white font-medium p-3 sm:px-4 sm:py-3 rounded-full shadow-lg sm:animate-pulse"
                >
                    <Phone className="w-4 h-4" />
                    <span className="text-sm hidden sm:inline">{s.fab}</span>
                </button>
            )}

            {open && (
                <div
                    ref={panelRef}
                    data-testid="callback-panel"
                    className="fixed left-4 bottom-4 sm:left-6 sm:bottom-6 z-[60] w-[92vw] max-w-sm bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden"
                >
                    <div className="flex items-center justify-between bg-amber-600 text-white px-4 py-3">
                        <div>
                            <div className="text-sm font-semibold">{s.title}</div>
                            <div className="text-xs opacity-90">{s.subtitle}</div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="callback-close-btn"
                            className="text-white/80 hover:text-white"
                            aria-label={s.close}
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {sent ? (
                        <div className="p-6 text-center" data-testid="callback-success">
                            <CheckCircle2 className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                            <div className="font-semibold text-gray-800">{s.success}</div>
                            <div className="text-sm text-gray-600 mt-1">{s.successDetail}</div>
                            <button
                                type="button"
                                onClick={reset}
                                data-testid="callback-reset-btn"
                                className="mt-4 text-sm bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg"
                            >
                                {s.close}
                            </button>
                        </div>
                    ) : (
                        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.name}</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={s.namePh}
                                    data-testid="callback-name-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.email}</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={s.emailPh}
                                    data-testid="callback-email-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.phone}</label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder={s.phonePh}
                                    data-testid="callback-phone-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">{s.org}</label>
                                <input
                                    type="text"
                                    value={org}
                                    onChange={(e) => setOrg(e.target.value)}
                                    placeholder={s.orgPh}
                                    data-testid="callback-org-input"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500"
                                />
                            </div>

                            {error && (
                                <div className="text-xs text-red-600" data-testid="callback-error">{error}</div>
                            )}

                            <button
                                type="button"
                                onClick={handleSubmit}
                                disabled={sending}
                                data-testid="callback-submit-btn"
                                className="w-full flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white font-medium px-4 py-2.5 rounded-lg text-sm"
                            >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                                {s.submit}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
